import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { 
  Search, 
  RefreshCw, 
  Mail, 
  Phone,
  User,
  Users,
  UserPlus,
  UserCheck,
  Calendar,
  MapPin,
  Globe,
  Edit2,
  Trash2,
  Send,
  Download,
  Upload,
  Filter,
  X,
  Check,
  AlertCircle,
  Clock,
  ArrowUpDown,
  Grid,
  List as ListIcon,
  MoreVertical,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Save,
  Plus,
  Link2,
  Loader
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorContacts.css';

/**
 * CuratorContacts
 * 
 * Comprehensive interface for curators to manage external organization members.
 * These are contacts from partner organizations (like CAVA) that haven't yet
 * become full VibrantCast users.
 * 
 * Features:
 * - View all external members with search and filters
 * - Add new contacts
 * - Edit existing contacts
 * - Track conversion status (linked to VibrantCast users)
 * - Send communications
 * - Import/Export capabilities
 */

const CuratorContacts = () => {
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};
  
  // Data state
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [conversionFilter, setConversionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [activeDropdown, setActiveDropdown] = useState(null); // Track which dropdown is open
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  
  // Bulk account creation state
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [bulkCreateLimit, setBulkCreateLimit] = useState(10);
  const [bulkCreateLoading, setBulkCreateLoading] = useState(false);
  const [bulkCreateResult, setBulkCreateResult] = useState(null);
  const [bulkCreateDryRun, setBulkCreateDryRun] = useState(null);
  const [linkingContactId, setLinkingContactId] = useState(null);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [exporting, setExporting] = useState(false);
  const downloadDropdownRef = useRef(null);
  const filtersRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    additional_emails: [],
    phone_primary: '',
    additional_phones: [],
    street: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    membership_status: 'active',
    join_date: '',
    renewal_date: '',
    auto_renew: false,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    converted: 0,
    pendingRenewal: 0
  });

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Get the effective renewal date (auto-renew contacts get advanced if past due)
  const getEffectiveRenewalDate = (contact) => {
    if (!contact.renewal_date) return null;
    
    const renewal = new Date(contact.renewal_date);
    const now = new Date();
    
    // If auto-renew and renewal is in the past, advance by one year
    if (contact.auto_renew && renewal < now) {
      const advancedDate = new Date(renewal);
      advancedDate.setFullYear(advancedDate.getFullYear() + 1);
      return advancedDate;
    }
    
    return renewal;
  };

  // Check if renewal is pending (within next month from today)
  const isRenewalPending = (contact) => {
    const effectiveRenewal = getEffectiveRenewalDate(contact);
    if (!effectiveRenewal) return false;
    
    const now = new Date();
    const oneMonthFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    
    // Renewal is pending if it's between today and 30 days from now
    return effectiveRenewal >= now && effectiveRenewal <= oneMonthFromNow;
  };

  // Load contacts
  const loadContacts = async () => {
    if (!selectedOrganization?.organization_id) {
      setContacts([]);
      setStats({ total: 0, active: 0, inactive: 0, converted: 0, pendingRenewal: 0 });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('external_organization_members')
        .select(`
          *,
          linked_user:user_uuid (
            uuid,
            first_name,
            last_name,
            email,
            profile_img
          )
        `)
        .eq('organization_id', selectedOrganization.organization_id)
        .order('last_name', { ascending: true });

      if (fetchError) throw fetchError;

      setContacts(data || []);
      
      // Calculate stats using effective renewal dates
      const newStats = {
        total: data?.length || 0,
        active: data?.filter(c => c.membership_status === 'active').length || 0,
        inactive: data?.filter(c => c.membership_status !== 'active').length || 0,
        converted: data?.filter(c => c.user_uuid).length || 0,
        pendingRenewal: data?.filter(c => isRenewalPending(c)).length || 0
      };
      setStats(newStats);

    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [selectedOrganization?.organization_id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prepare export data
  const prepareExportData = () => {
    return filteredContacts.map(contact => ({
      'First Name': contact.first_name || '',
      'Last Name': contact.last_name || '',
      'Email': contact.email || '',
      'Additional Emails': (contact.additional_emails || []).join('; '),
      'Phone': contact.phone_primary || '',
      'Additional Phones': (contact.additional_phones || []).join('; '),
      'Street': contact.street || '',
      'City': contact.city || '',
      'State': contact.state || '',
      'ZIP': contact.zip || '',
      'Website': contact.website || '',
      'Membership Status': contact.membership_status || '',
      'Join Date': contact.join_date ? formatFullDate(contact.join_date) : '',
      'Renewal Date': contact.renewal_date ? formatFullDate(contact.renewal_date) : '',
      'Auto Renew': contact.auto_renew ? 'Yes' : 'No',
      'Linked to VibrantCast': contact.user_uuid ? 'Yes' : 'No',
      'Notes': contact.notes || ''
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      setExporting(true);
      const data = prepareExportData();
      
      if (data.length === 0) {
        alert('No contacts to export');
        return;
      }

      // Get headers from first row
      const headers = Object.keys(data[0]);
      
      // Build CSV content
      const csvRows = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            let cell = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (cell.toString().includes(',') || cell.toString().includes('"') || cell.toString().includes('\n')) {
              cell = '"' + cell.toString().replace(/"/g, '""') + '"';
            }
            return cell;
          }).join(',')
        )
      ];
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = `${selectedOrganization?.name || 'contacts'}_contacts_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename.replace(/[^a-z0-9_.-]/gi, '_'));
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setShowDownloadDropdown(false);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Error exporting to CSV: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      setExporting(true);
      const data = prepareExportData();
      
      if (data.length === 0) {
        alert('No contacts to export');
        return;
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      
      // Set column widths
      const colWidths = [
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 30 }, // Email
        { wch: 30 }, // Additional Emails
        { wch: 15 }, // Phone
        { wch: 20 }, // Additional Phones
        { wch: 25 }, // Street
        { wch: 15 }, // City
        { wch: 10 }, // State
        { wch: 10 }, // ZIP
        { wch: 25 }, // Website
        { wch: 15 }, // Membership Status
        { wch: 12 }, // Join Date
        { wch: 12 }, // Renewal Date
        { wch: 10 }, // Auto Renew
        { wch: 18 }, // Linked to VibrantCast
        { wch: 30 }, // Notes
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
      
      const filename = `${selectedOrganization?.name || 'contacts'}_contacts_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename.replace(/[^a-z0-9_.-]/gi, '_'));
      
      setShowDownloadDropdown(false);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      alert('Error exporting to Excel: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    let filtered = [...contacts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.first_name?.toLowerCase().includes(query) ||
        c.last_name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.city?.toLowerCase().includes(query) ||
        c.phone_primary?.includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending_renewal') {
        // Filter for pending renewals (next 30 days, accounting for auto-renew)
        filtered = filtered.filter(c => isRenewalPending(c));
      } else {
        filtered = filtered.filter(c => c.membership_status === statusFilter);
      }
    }

    // Conversion filter
    if (conversionFilter === 'converted') {
      filtered = filtered.filter(c => c.user_uuid);
    } else if (conversionFilter === 'not_converted') {
      filtered = filtered.filter(c => !c.user_uuid);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
        case 'last_name':
          aVal = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
          bVal = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'city':
          aVal = a.city?.toLowerCase() || '';
          bVal = b.city?.toLowerCase() || '';
          break;
        case 'join_date':
          aVal = a.join_date || '';
          bVal = b.join_date || '';
          break;
        case 'renewal_date':
          aVal = a.renewal_date || '';
          bVal = b.renewal_date || '';
          break;
        case 'status':
          aVal = a.membership_status || '';
          bVal = b.membership_status || '';
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [contacts, searchQuery, statusFilter, conversionFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredContacts.slice(start, start + itemsPerPage);
  }, [filteredContacts, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, conversionFilter]);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add new contact
  const handleAddContact = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const { data, error: insertError } = await supabase
        .from('external_organization_members')
        .insert({
          organization_id: selectedOrganization.organization_id,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim().toLowerCase(),
          additional_emails: formData.additional_emails.filter(e => e.trim()),
          phone_primary: formData.phone_primary?.trim() || null,
          additional_phones: formData.additional_phones.filter(p => p.trim()),
          street: formData.street?.trim() || null,
          city: formData.city?.trim() || null,
          state: formData.state?.trim() || null,
          zip: formData.zip?.trim() || null,
          website: formData.website?.trim() || null,
          membership_status: formData.membership_status,
          join_date: formData.join_date || null,
          renewal_date: formData.renewal_date || null,
          auto_renew: formData.auto_renew,
          notes: formData.notes?.trim() || null,
          import_source: 'manual'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setContacts(prev => [...prev, data]);
      setShowAddModal(false);
      resetForm();
      loadContacts(); // Refresh to get updated stats
      
    } catch (err) {
      console.error('Error adding contact:', err);
      setFormErrors({ submit: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Update contact
  const handleUpdateContact = async () => {
    if (!validateForm() || !editingContact) return;

    try {
      setSaving(true);

      const { data, error: updateError } = await supabase
        .from('external_organization_members')
        .update({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim().toLowerCase(),
          additional_emails: formData.additional_emails.filter(e => e.trim()),
          phone_primary: formData.phone_primary?.trim() || null,
          additional_phones: formData.additional_phones.filter(p => p.trim()),
          street: formData.street?.trim() || null,
          city: formData.city?.trim() || null,
          state: formData.state?.trim() || null,
          zip: formData.zip?.trim() || null,
          website: formData.website?.trim() || null,
          membership_status: formData.membership_status,
          join_date: formData.join_date || null,
          renewal_date: formData.renewal_date || null,
          auto_renew: formData.auto_renew,
          notes: formData.notes?.trim() || null
        })
        .eq('id', editingContact.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setContacts(prev => prev.map(c => c.id === editingContact.id ? data : c));
      setShowEditModal(false);
      setEditingContact(null);
      resetForm();
      
    } catch (err) {
      console.error('Error updating contact:', err);
      setFormErrors({ submit: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete contact
  const handleDeleteContact = async (contact) => {
    try {
      const { error: deleteError } = await supabase
        .from('external_organization_members')
        .delete()
        .eq('id', contact.id);

      if (deleteError) throw deleteError;

      setContacts(prev => prev.filter(c => c.id !== contact.id));
      setShowDeleteConfirm(null);
      loadContacts(); // Refresh stats
      
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError(err.message);
    }
  };

  // Navigate to contact detail page
  const openEditModal = (contact) => {
    navigate(`/curator/contacts/${contact.id}`);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      additional_emails: [],
      phone_primary: '',
      additional_phones: [],
      street: '',
      city: '',
      state: '',
      zip: '',
      website: '',
      membership_status: 'active',
      join_date: '',
      renewal_date: '',
      auto_renew: false,
      notes: ''
    });
    setFormErrors({});
  };

  // Toggle contact selection
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible contacts
  const toggleSelectAll = () => {
    if (selectedContacts.size === paginatedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(paginatedContacts.map(c => c.id)));
    }
  };

  // Bulk create VibrantCast accounts
  const handleBulkAccountCreate = async (isDryRun = false) => {
    if (!selectedOrganization?.organization_id) return;
    
    setBulkCreateLoading(true);
    setBulkCreateResult(null);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setBulkCreateResult({ success: false, error: 'Not authenticated. Please log in again.' });
        setBulkCreateLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/member_bulk_account_create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            organization_id: selectedOrganization.organization_id,
            limit: bulkCreateLimit,
            dry_run: isDryRun
          })
        }
      );
      
      const result = await response.json();
      
      if (isDryRun) {
        setBulkCreateDryRun(result);
      } else {
        setBulkCreateResult(result);
        if (result.success && result.created > 0) {
          // Refresh contacts to show updated linked status
          await loadContacts();
        }
      }
    } catch (err) {
      console.error('Bulk account creation error:', err);
      setBulkCreateResult({ success: false, error: err.message });
    } finally {
      setBulkCreateLoading(false);
    }
  };

  // Create account for single contact
  const handleCreateSingleAccount = async (contactId) => {
    if (!selectedOrganization?.organization_id) return;
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        return { success: false, error: 'Not authenticated. Please log in again.' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/member_bulk_account_create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            organization_id: selectedOrganization.organization_id,
            member_ids: [contactId]
          })
        }
      );
      
      const result = await response.json();
      if (result.success && result.created > 0) {
        await loadContacts();
      }
      return result;
    } catch (err) {
      console.error('Single account creation error:', err);
      return { success: false, error: err.message };
    }
  };

  // Get initials for avatar
  const getInitials = (contact) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  // Render status badge
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      active: { color: 'green', label: 'Active' },
      inactive: { color: 'gray', label: 'Inactive' },
      pending: { color: 'yellow', label: 'Pending' },
      expired: { color: 'red', label: 'Expired' },
      suspended: { color: 'red', label: 'Suspended' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    
    return (
      <span className={`contact-status-badge status-${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Render conversion badge
  const ConversionBadge = ({ contact }) => {
    if (contact.user_uuid) {
      return (
        <span className="contact-conversion-badge converted">
          <UserCheck size={12} />
          <span>VibrantCast User</span>
        </span>
      );
    }
    return (
      <span className="contact-conversion-badge not-converted">
        <User size={12} />
        <span>Contact Only</span>
      </span>
    );
  };

  // Render auto-renewal badge
  const AutoRenewBadge = ({ isAutoRenew }) => {
    if (!isAutoRenew) return null;
    
    return (
      <span className="contact-auto-renew-badge" title="Auto-renewal enabled">
        <RefreshCw size={12} />
        <span>Auto-Renew</span>
      </span>
    );
  };

  // Contact card for grid view
  const ContactCard = ({ contact }) => (
    <div 
      className={`contact-card-instagram ${selectedContacts.has(contact.id) ? 'selected' : ''}`}
      onClick={() => toggleContactSelection(contact.id)}
    >
      <div className="contact-card-header">
        <div className="contact-avatar-wrapper">
          {contact.linked_user?.profile_img ? (
            <img 
              src={contact.linked_user.profile_img} 
              alt={`${contact.first_name} ${contact.last_name}`}
              className="contact-avatar"
            />
          ) : (
            <div className="contact-avatar-placeholder">
              {getInitials(contact)}
            </div>
          )}
          {contact.user_uuid && (
            <div className="contact-linked-badge">
              <Link2 size={10} />
            </div>
          )}
        </div>
        
        <div className="contact-card-actions">
          <button
            className="contact-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(contact);
            }}
            title="Edit contact"
          >
            <Edit2 size={14} />
          </button>
          <button
            className="contact-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/curator/contacts/email?ids=${contact.id}`);
            }}
            title="Send email"
          >
            <Mail size={14} />
          </button>
        </div>
      </div>
      
      <div className="contact-card-body">
        <h3 className="contact-name">
          {contact.first_name} {contact.last_name}
        </h3>
        <p className="contact-email">{contact.email}</p>
        
        {contact.city && (
          <p className="contact-location">
            <MapPin size={12} />
            {contact.city}{contact.state ? `, ${contact.state}` : ''}
          </p>
        )}
      </div>
      
      <div className="contact-card-stats">
        <div className="contact-stat">
          <span className="stat-label">Status</span>
          <StatusBadge status={contact.membership_status} />
        </div>
        <div className="contact-stat-divider" />
        <div className="contact-stat">
          <span className="stat-label">Member since</span>
          <span className="stat-value">{formatFullDate(contact.join_date)}</span>
        </div>
        <div className="contact-stat-divider" />
        <div className="contact-stat">
          <span className="stat-label">Renewal</span>
          <span className={`stat-value ${isRenewalPending(contact) ? 'renewal-pending' : ''}`}>
            {formatFullDate(getEffectiveRenewalDate(contact) || contact.renewal_date)}
          </span>
        </div>
      </div>
      
      <div className="contact-card-footer">
        <ConversionBadge contact={contact} />
        <AutoRenewBadge isAutoRenew={contact.auto_renew} />
        {contact.website && (
          <a 
            href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-website-link"
            onClick={(e) => e.stopPropagation()}
          >
            <Globe size={12} />
            Website
          </a>
        )}
      </div>
    </div>
  );

  // Contact row for list view - compact layout
  const ContactRow = ({ contact }) => (
    <tr 
      className={`contact-row ${selectedContacts.has(contact.id) ? 'selected' : ''}`}
      onClick={() => openEditModal(contact)}
      style={{ cursor: 'pointer' }}
    >
      <td className="contact-checkbox-cell" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedContacts.has(contact.id)}
          onChange={() => toggleContactSelection(contact.id)}
        />
      </td>
      <td className="contact-name-cell">
        <div className="contact-name-wrapper">
          {contact.linked_user?.profile_img ? (
            <img 
              src={contact.linked_user.profile_img} 
              alt={`${contact.first_name} ${contact.last_name}`}
              className="contact-avatar-small"
            />
          ) : (
            <div className="contact-avatar-small-placeholder">
              {getInitials(contact)}
            </div>
          )}
          <div className="contact-name-info">
            <span className="contact-full-name">
              {contact.first_name} {contact.last_name}
            </span>
            <div className="contact-name-meta">
              <StatusBadge status={contact.membership_status} />
              <AutoRenewBadge isAutoRenew={contact.auto_renew} />
              {contact.user_uuid && (
                <span className="contact-linked-indicator">
                  <Link2 size={10} /> Linked
                </span>
              )}
            </div>
            <div className="contact-dates-inline">
              <span>Joined: {formatFullDate(contact.join_date)}</span>
              <span className={isRenewalPending(contact) ? 'renewal-pending' : ''}>
                Renewal: {formatFullDate(getEffectiveRenewalDate(contact) || contact.renewal_date)}
              </span>
            </div>
          </div>
        </div>
      </td>
      <td className="contact-info-cell">
        <div className="contact-info-stack">
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/curator/contacts/email?ids=${contact.id}`);
            }} 
            className="contact-info-primary"
          >
            <Mail size={12} /> {contact.email}
          </a>
          {contact.phone_primary && (
            <a href={`tel:${contact.phone_primary}`} onClick={(e) => e.stopPropagation()} className="contact-info-secondary">
              <Phone size={12} /> {contact.phone_primary}
            </a>
          )}
          {contact.city && (
            <span className="contact-info-secondary">
              <MapPin size={12} /> {contact.city}{contact.state ? `, ${contact.state}` : ''}
            </span>
          )}
        </div>
      </td>
      <td className="contact-actions-cell">
        <div className="row-actions">
          {!contact.user_uuid && (
            <button
              className="row-action-btn link-btn"
              onClick={async (e) => {
                e.stopPropagation();
                setLinkingContactId(contact.id);
                const result = await handleCreateSingleAccount(contact.id);
                setLinkingContactId(null);
                if (result?.success) {
                  // Show brief success indication
                  console.log('✅ Account created for', contact.email);
                }
              }}
              disabled={linkingContactId === contact.id}
              title="Create VibrantCast Account"
            >
              {linkingContactId === contact.id ? (
                <Loader size={14} className="spin" />
              ) : (
                <Link2 size={14} />
              )}
            </button>
          )}
          <button
            className="row-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(contact);
            }}
            title="Edit"
          >
            <Edit2 size={14} />
          </button>
          <button
            className="row-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/curator/contacts/email?ids=${contact.id}`);
            }}
            title="Email"
          >
            <Mail size={14} />
          </button>
          <button
            className="row-action-btn danger"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(contact);
            }}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );

  // Add/Edit Modal
  const ContactFormModal = ({ isEdit = false }) => (
    <div className="modal-overlay" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>
      <div className="modal-content contact-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Contact' : 'Add New Contact'}</h2>
          <button 
            className="modal-close-btn"
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {formErrors.submit && (
            <div className="form-error-banner">
              <AlertCircle size={16} />
              {formErrors.submit}
            </div>
          )}
          
          <div className="form-section">
            <h3 className="form-section-title">
              <User size={16} />
              Basic Information
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className={formErrors.first_name ? 'input-error' : ''}
                  placeholder="Enter first name"
                />
                {formErrors.first_name && <span className="field-error">{formErrors.first_name}</span>}
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className={formErrors.last_name ? 'input-error' : ''}
                  placeholder="Enter last name"
                />
                {formErrors.last_name && <span className="field-error">{formErrors.last_name}</span>}
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <Mail size={16} />
              Contact Details
            </h3>
            <div className="form-group full-width">
              <label>Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={formErrors.email ? 'input-error' : ''}
                placeholder="email@example.com"
              />
              {formErrors.email && <span className="field-error">{formErrors.email}</span>}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone_primary}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_primary: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <MapPin size={16} />
              Address
            </h3>
            <div className="form-group full-width">
              <label>Street Address</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>
            <div className="form-grid three-cols">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Chicago"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="IL"
                  maxLength={2}
                />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                  placeholder="60601"
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <Calendar size={16} />
              Membership
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.membership_status}
                  onChange={(e) => setFormData(prev => ({ ...prev, membership_status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.auto_renew}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_renew: e.target.checked }))}
                  />
                  Auto-renew membership
                </label>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Join Date</label>
                <input
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, join_date: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Renewal Date</label>
                <input
                  type="date"
                  value={formData.renewal_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, renewal_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <Edit2 size={16} />
              Notes
            </h3>
            <div className="form-group full-width">
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about this contact..."
                rows={3}
              />
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="btn-secondary"
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={isEdit ? handleUpdateContact : handleAddContact}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEdit ? 'Update Contact' : 'Add Contact'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Delete confirmation modal
  const DeleteConfirmModal = () => (
    <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
      <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-confirm-icon">
          <AlertCircle size={48} />
        </div>
        <h3>Delete Contact?</h3>
        <p>
          Are you sure you want to delete <strong>{showDeleteConfirm?.first_name} {showDeleteConfirm?.last_name}</strong>?
          This action cannot be undone.
        </p>
        <div className="delete-confirm-actions">
          <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
            Cancel
          </button>
          <button className="btn-danger" onClick={() => handleDeleteContact(showDeleteConfirm)}>
            <Trash2 size={16} />
            Delete Contact
          </button>
        </div>
      </div>
    </div>
  );

  // Empty state
  if (!selectedOrganization?.organization_id) {
    return (
      <div className="curator-contacts">
        <div className="empty-state">
          <Users size={48} />
          <h3>No Organization Selected</h3>
          <p>Please select an organization to view contacts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-contacts">
      {/* Header */}
      <div className="contacts-header">
        <div className="header-content">
          <h1 className="contacts-title">{selectedOrganization?.name || 'Organization'} Contacts</h1>
        </div>
        <div className="header-actions">
          <button 
            className="refresh-button-icon"
            onClick={loadContacts}
            title="Refresh contacts"
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
          
          {/* Download Dropdown */}
          <div className="download-dropdown-container" ref={downloadDropdownRef}>
            <button 
              className="btn-secondary"
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              disabled={exporting || filteredContacts.length === 0}
              title="Download contacts"
            >
              {exporting ? (
                <Loader size={16} className="spin" />
              ) : (
                <Download size={16} />
              )}
              Download
            </button>
            {showDownloadDropdown && (
              <div className="download-dropdown-menu">
                <button onClick={exportToCSV} disabled={exporting}>
                  <Download size={14} />
                  Download CSV
                </button>
                <button onClick={exportToExcel} disabled={exporting}>
                  <Download size={14} />
                  Download Excel
                </button>
              </div>
            )}
          </div>
          
          <button 
            className="btn-primary"
            onClick={() => navigate('/curator/contacts/new')}
          >
            <UserPlus size={16} />
            Add Contact
          </button>
        </div>
      </div>

      {/* Stats Cards - Clickable to filter */}
      <div className="contacts-stats">
        <button 
          className={`stat-card clickable ${statusFilter === 'all' && conversionFilter === 'all' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('all'); setConversionFilter('all'); }}
        >
          <div className="stat-icon">
            <Users size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Contacts →</span>
          </div>
        </button>
        <button 
          className={`stat-card clickable ${statusFilter === 'active' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('active'); setConversionFilter('all'); }}
        >
          <div className="stat-icon green">
            <UserCheck size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.active}</span>
            <span className="stat-label">Active Members →</span>
          </div>
        </button>
        <button 
          className={`stat-card clickable ${conversionFilter === 'converted' ? 'active' : ''}`}
          onClick={() => { setConversionFilter('converted'); setStatusFilter('all'); }}
        >
          <div className="stat-icon purple">
            <Link2 size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.converted}</span>
            <span className="stat-label">VibrantCast Users →</span>
          </div>
        </button>
        <button 
          className={`stat-card clickable ${statusFilter === 'pending_renewal' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('pending_renewal'); setConversionFilter('all'); }}
        >
          <div className="stat-icon orange">
            <Clock size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.pendingRenewal}</span>
            <span className="stat-label">Pending Renewal →</span>
          </div>
        </button>
      </div>

      {/* Bulk Create Accounts Action */}
      {stats.total > stats.converted && (
        <div className="bulk-create-action">
          <div className="bulk-create-info">
            <AlertCircle size={16} />
            <span>{stats.total - stats.converted} contacts without VibrantCast accounts</span>
          </div>
          <button 
            className="btn-secondary"
            onClick={() => setShowBulkCreateModal(true)}
          >
            <UserPlus size={16} />
            Create Accounts
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="contacts-controls">
        <div className="controls-row">
          <div className="search-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="search-clear"
                onClick={() => setSearchQuery('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        
        <div className="controls-row filters-row">
          {/* Compact icon-based filters */}
          <div className="icon-filters" ref={filtersRef}>
            <div className="filter-dropdown">
              <button 
                className={`icon-filter-btn ${statusFilter !== 'all' ? 'active' : ''}`}
                title="Filter by status"
                onClick={() => setActiveDropdown(prev => prev === 'status' ? null : 'status')}
              >
                <Filter size={16} />
              </button>
              {activeDropdown === 'status' && (
                <div className="filter-dropdown-menu">
                  <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => { setStatusFilter('all'); setActiveDropdown(null); }}>All Statuses</button>
                  <button className={statusFilter === 'active' ? 'active' : ''} onClick={() => { setStatusFilter('active'); setActiveDropdown(null); }}>Active</button>
                  <button className={statusFilter === 'inactive' ? 'active' : ''} onClick={() => { setStatusFilter('inactive'); setActiveDropdown(null); }}>Inactive</button>
                  <button className={statusFilter === 'pending' ? 'active' : ''} onClick={() => { setStatusFilter('pending'); setActiveDropdown(null); }}>Pending</button>
                  <button className={statusFilter === 'expired' ? 'active' : ''} onClick={() => { setStatusFilter('expired'); setActiveDropdown(null); }}>Expired</button>
                </div>
              )}
            </div>
            
            <div className="filter-dropdown">
              <button 
                className={`icon-filter-btn ${conversionFilter !== 'all' ? 'active' : ''}`}
                title="Filter by user type"
                onClick={() => setActiveDropdown(prev => prev === 'conversion' ? null : 'conversion')}
              >
                <UserCheck size={16} />
              </button>
              {activeDropdown === 'conversion' && (
                <div className="filter-dropdown-menu">
                  <button className={conversionFilter === 'all' ? 'active' : ''} onClick={() => { setConversionFilter('all'); setActiveDropdown(null); }}>All Contacts</button>
                  <button className={conversionFilter === 'converted' ? 'active' : ''} onClick={() => { setConversionFilter('converted'); setActiveDropdown(null); }}>VibrantCast Users</button>
                  <button className={conversionFilter === 'not_converted' ? 'active' : ''} onClick={() => { setConversionFilter('not_converted'); setActiveDropdown(null); }}>Contacts Only</button>
                </div>
              )}
            </div>
            
            <div className="filter-dropdown">
              <button 
                className="icon-filter-btn"
                title="Sort by"
                onClick={() => setActiveDropdown(prev => prev === 'sort' ? null : 'sort')}
              >
                <ArrowUpDown size={16} />
              </button>
              {activeDropdown === 'sort' && (
                <div className="filter-dropdown-menu">
                  <button className={sortBy === 'last_name' ? 'active' : ''} onClick={() => { setSortBy('last_name'); setActiveDropdown(null); }}>Name</button>
                  <button className={sortBy === 'email' ? 'active' : ''} onClick={() => { setSortBy('email'); setActiveDropdown(null); }}>Email</button>
                  <button className={sortBy === 'city' ? 'active' : ''} onClick={() => { setSortBy('city'); setActiveDropdown(null); }}>City</button>
                  <button className={sortBy === 'join_date' ? 'active' : ''} onClick={() => { setSortBy('join_date'); setActiveDropdown(null); }}>Join Date</button>
                  <button className={sortBy === 'renewal_date' ? 'active' : ''} onClick={() => { setSortBy('renewal_date'); setActiveDropdown(null); }}>Renewal</button>
                </div>
              )}
            </div>
            
            <button
              className="icon-filter-btn"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
            
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <Grid size={16} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <ListIcon size={16} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Filter summary line */}
        <div className="filter-summary">
          <span className="filter-summary-text">
            Showing: <strong>{filteredContacts.length}</strong> {filteredContacts.length === 1 ? 'contact' : 'contacts'}
            {statusFilter !== 'all' && (
              <span className="filter-tag">
                {statusFilter === 'pending_renewal' ? 'Pending Renewal' : statusFilter}
                <button onClick={() => setStatusFilter('all')}><X size={10} /></button>
              </span>
            )}
            {conversionFilter !== 'all' && (
              <span className="filter-tag">
                {conversionFilter === 'converted' ? 'VibrantCast Users' : 'Contacts Only'}
                <button onClick={() => setConversionFilter('all')}><X size={10} /></button>
              </span>
            )}
            {searchQuery && (
              <span className="filter-tag">
                "{searchQuery}"
                <button onClick={() => setSearchQuery('')}><X size={10} /></button>
              </span>
            )}
            <span className="sort-info">
              sorted by {sortBy === 'last_name' ? 'name' : sortBy.replace('_', ' ')} {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
          </span>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedContacts.size > 0 && (
        <div className="bulk-actions-bar">
          <span className="selection-count">
            {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
          </span>
          <div className="bulk-actions">
            <button 
              className="bulk-action-btn"
              onClick={() => {
                const ids = Array.from(selectedContacts).join(',');
                navigate(`/curator/contacts/email?ids=${ids}`);
              }}
            >
              <Mail size={14} />
              Email Selected
            </button>
            <button 
              className="bulk-action-btn danger"
              onClick={() => setSelectedContacts(new Set())}
            >
              <X size={14} />
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="loading-state">
          <Loader size={32} className="spin" />
          <p>Loading contacts...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>{searchQuery || statusFilter !== 'all' || conversionFilter !== 'all' ? 'No Matching Contacts' : 'No Contacts Yet'}</h3>
          <p>
            {searchQuery || statusFilter !== 'all' || conversionFilter !== 'all' 
              ? 'Try adjusting your search or filters.'
              : 'Add your first contact to get started.'}
          </p>
          {!searchQuery && statusFilter === 'all' && conversionFilter === 'all' && (
            <button 
              className="btn-primary"
              onClick={() => navigate('/curator/contacts/new')}
            >
              <UserPlus size={16} />
              Add First Contact
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="contacts-grid">
              {paginatedContacts.map(contact => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="contacts-table-wrapper">
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th className="checkbox-header">
                      <input
                        type="checkbox"
                        checked={selectedContacts.size === paginatedContacts.length && paginatedContacts.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedContacts.map(contact => (
                    <ContactRow key={contact.id} contact={contact} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredContacts.length)} of {filteredContacts.length}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="pagination-size">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && <ContactFormModal isEdit={false} />}
      {showEditModal && <ContactFormModal isEdit={true} />}
      {showDeleteConfirm && <DeleteConfirmModal />}

      {/* Bulk Create Accounts Modal */}
      {showBulkCreateModal && (
        <div className="modal-overlay" onClick={() => !bulkCreateLoading && setShowBulkCreateModal(false)}>
          <div className="modal-content bulk-create-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create VibrantCast Accounts</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowBulkCreateModal(false)}
                disabled={bulkCreateLoading}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="bulk-create-description">
                Create VibrantCast accounts for organization members who don't have one yet.
                This will auto-confirm their email and set them up as artists.
              </p>
              
              <div className="bulk-create-stats">
                <div className="stat-item">
                  <span className="stat-label">Active members without accounts:</span>
                  <span className="stat-value">{stats.active - stats.converted}</span>
                </div>
              </div>

              <div className="bulk-create-options">
                <label className="form-label">
                  Number of accounts to create:
                  <select 
                    value={bulkCreateLimit} 
                    onChange={(e) => setBulkCreateLimit(Number(e.target.value))}
                    disabled={bulkCreateLoading}
                  >
                    <option value={1}>1 (Test)</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={0}>All remaining</option>
                  </select>
                </label>
              </div>

              {bulkCreateDryRun && (
                <div className="bulk-create-preview">
                  <h4>Preview ({bulkCreateDryRun.would_create} accounts)</h4>
                  <div className="preview-list">
                    {bulkCreateDryRun.members?.slice(0, 10).map((m, i) => (
                      <div key={i} className="preview-item">
                        <span>{m.first_name} {m.last_name}</span>
                        <span className="preview-email">{m.email}</span>
                      </div>
                    ))}
                    {bulkCreateDryRun.members?.length > 10 && (
                      <div className="preview-more">...and {bulkCreateDryRun.members.length - 10} more</div>
                    )}
                  </div>
                </div>
              )}

              {bulkCreateResult && (
                <div className={`bulk-create-result ${bulkCreateResult.success ? 'success' : 'error'}`}>
                  {bulkCreateResult.success ? (
                    <>
                      <Check size={16} />
                      <span>{bulkCreateResult.message}</span>
                      {bulkCreateResult.remaining > 0 && (
                        <span className="result-remaining">({bulkCreateResult.remaining} remaining)</span>
                      )}
                    </>
                  ) : (
                    <>
                      <AlertCircle size={16} />
                      <span>Error: {bulkCreateResult.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => handleBulkAccountCreate(true)}
                disabled={bulkCreateLoading}
              >
                {bulkCreateLoading ? <Loader size={16} className="spin" /> : 'Preview'}
              </button>
              <button 
                className="btn-primary"
                onClick={() => handleBulkAccountCreate(false)}
                disabled={bulkCreateLoading}
              >
                {bulkCreateLoading ? (
                  <><Loader size={16} className="spin" /> Creating...</>
                ) : (
                  <>Create Accounts</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorContacts;
